/**
 * @file     zLayers.ts
 * @readme   ./README.md
 * @purpose  Single ordered z-index scale for the whole app. The numeric value
 *           of a surface is decided HERE, never inline, so the stacking order
 *           is auditable in one place and surfaces stop fighting each other.
 */

/**
 * One authoritative stacking order, low to high. The standard names
 * (`appBar`, `drawerScrim`, `modal`, `snackbar`, `tooltip`) intentionally
 * match MUI's default `theme.zIndex` so MUI components and our own surfaces
 * share one ruler — `createMuiTheme` feeds these same values back into the
 * theme.
 *
 * Rule of thumb when adding a surface: pick the band it belongs to, not a
 * bigger number than whatever it happened to overlap in testing. Navigation
 * (appBar/drawer) must always beat in-page content and map chrome; modals beat
 * navigation; gates/lightbox sit above modals on purpose.
 */
export const zLayers = {
  belowContent: -1,
  base: 0,
  content: 1,

  // --- Map internals: local stacking inside the map container (0-99 band) ---
  // On phone the map pane and the results/detail pane are absolutely-positioned
  // SIBLINGS in the page flex row; these two decide which pane wins. Everything
  // else in this band stacks *inside* one of those two panes' own contexts.
  mapContainer: 0, // mobile full-screen map pane
  mapControls: 10, // zoom cluster, chip rail, transit legend, priority pill rail
  mapControlsAbove: 11, // find-area pill + filters entry, just above the chip rail
  mapLayerSwitch: 15, // layer-switch popover — above the chip rail + zoom cluster
  mapChrome: 20, // draw-mode banner
  // AI Search panel docked over the map's own list/chrome (Phase 1). Local to
  // the results pane, so it sits under the navbar and global modals.
  mapAiPanel: 40,
  // The pin hover preview renders through a document.body portal, so it escapes
  // the map's local stacking context and needs a real global value. It must sit
  // BELOW the navbar and drawer (a map preview never covers navigation) and
  // BELOW the results/detail panel on ALL breakpoints (the panel holds mapPanel
  // everywhere; a map popup never covers the chat/list/detail sheet).
  mapHoverCard: 1095,

  // --- Client record (local to the admin content scroller) ---
  clientRecordRail: 18,
  clientRecordSectionBar: 19,
  clientRecordIdentity: 20,

  // --- Sticky page furniture, below the navbar ---
  stickyBar: 1050,
  // Results/AI panel (bottom sheet on phone, side pane on desktop). Held at ALL
  // breakpoints so it lives in the GLOBAL band ABOVE the pin hover card (a map
  // popup must never cover the chat/list/detail sheet) and below the navbar.
  // The compare tray sits just above it (a committed action bar the sheet must
  // not hide).
  mapPanel: 1096,
  compareTray: 1097,

  // --- Navigation system ---
  appBar: 1100, // navbar (== MUI appBar default)
  announcement: 1110, // announcement-bar dropdown, just above the navbar
  drawerScrim: 1200, // mobile nav drawer backdrop (== MUI drawer default)
  drawerPanel: 1201,

  // --- MUI overlay defaults (kept in sync via createMuiTheme) ---
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,

  // --- Full-screen / conversion surfaces, layered above modals on purpose ---
  overlay: 1600, // full-screen map overlay, in-page map modal
  navModal: 1650, // home-finder modal launched from the navbar
  gate: 1700, // auth + marketing gates (listings gate, password gate, welcome)
  leadCapture: 1750, // lead-capture / pre-approval / submit-offer flows
  lightbox: 1800, // photo lightbox — always on top
} as const;

export type ZLayer = keyof typeof zLayers;
